/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { listFiles, listFilesNext } from 'store/slices/files';
import { fetchFileList, getSearchKey } from 'utils/file-list';

export const useArtworkFilesData = ({ activeTab, selectedFolder, sortKey, sortDirection, selectedProperty }) => {
  const dispatch = useDispatch();

  const [fileListMap, setFileListMap] = useState({});
  const [loadingFiles, setLoadingFiles] = useState(false);

  const sort = `${sortKey} ${sortDirection}`;
  const searchKey = useMemo(
    () => getSearchKey(activeTab, selectedFolder, selectedProperty?.id, sort),
    [activeTab, selectedFolder, selectedProperty, sort]
  );
  const currentFileList = fileListMap[searchKey] || { items: [], offset: 0, hasMore: true };
  const visibleFiles = useMemo(() => currentFileList.items || [], [currentFileList]);
  const hasMore = currentFileList.hasMore;
  const nextOffset = currentFileList.offset;

  const appendToFileList = (key, newFiles, offset, hasMore) => {
    setFileListMap((prev) => {
      const existingItems = prev[key]?.items || [];

      const existingOids = new Set(existingItems.map((file) => file.oid));

      const uniqueNewFiles = newFiles.filter((file) => !existingOids.has(file.oid));

      return {
        ...prev,
        [key]: {
          ...prev[key],
          items: [...existingItems, ...uniqueNewFiles],
          offset,
          hasMore,
        },
      };
    });
  };

  const updateFileList = (key, updatedFiles, offset, hasMore) => {
    setFileListMap((prev) => ({
      ...prev,
      [key]: { items: updatedFiles, offset, hasMore },
    }));
  };

  const buildParams = (offset) => {
    const effectivePropertyId = selectedProperty?.id;

    return {
      sort,
      content_types: 'image/jpeg,image/png',
      offset,
      ...(selectedFolder
        ? { parent: selectedFolder.oid }
        : // : searchString
          //   ? { search: searchString }
          activeTab === 'property'
          ? { property: effectivePropertyId }
          : { scope: activeTab }),
    };
  };

  const fetchMoreFiles = async () => {
    if (loadingFiles || !hasMore) return;
    if (nextOffset == null) return;

    setLoadingFiles(true);

    try {
      const params = buildParams(nextOffset);

      const {
        data: newFiles,
        offset,
        hasMore: newHasMore,
      } = await fetchFileList({
        dispatch,
        dispatchFunc: listFilesNext,
        dispatchParams: params,
      });

      appendToFileList(searchKey, newFiles, offset, newHasMore);
    } finally {
      setLoadingFiles(false);
    }
  };

  const fetchFiles = useCallback(async () => {
    setLoadingFiles(true);

    try {
      const effectivePropertyId = selectedProperty?.id;
      const currentKey = getSearchKey(activeTab, selectedFolder, effectivePropertyId, sort);

      if (fileListMap[currentKey]) return;

      const params = buildParams(0);

      const {
        data: updatedFiles,
        offset,
        hasMore,
      } = await fetchFileList({
        dispatch,
        dispatchFunc: listFiles,
        dispatchParams: params,
      });

      updateFileList(currentKey, updatedFiles, offset, hasMore);
    } finally {
      setLoadingFiles(false);
    }
  }, [activeTab, selectedFolder, sort, dispatch, selectedProperty, fileListMap]);

  useEffect(() => {
    if (!activeTab) return;
    if (activeTab === 'property' && !selectedProperty?.id) return;
    if (loadingFiles) return;

    const isCached = !!fileListMap[searchKey];
    if (isCached) return;

    fetchFiles();
  }, [activeTab, selectedFolder, selectedProperty, sort, fetchFiles, fileListMap, loadingFiles]);

  return {
    currentFiles: visibleFiles,
    fileMap: fileListMap,
    loadingFiles,
    hasMore,
    fetchMoreFiles,
  };
};
